import { useState } from "react";
import { Mail, Phone, MapPin, Check } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { CONTACT, mailtoConsult } from "@/lib/contact";
import { brand, contactCopy } from "@/lib/home-content";

const inputCls =
    "w-full h-12 rounded-xl border border-black/10 bg-white px-4 text-sm text-[#0a0a0b] placeholder:text-[#8a8a95] outline-none focus:border-[var(--brand)]/60 focus:ring-2 focus:ring-[var(--brand)]/20";

export default function Contact() {
    const { ref, visible } = useReveal();
    const [form, setForm] = useState({ name: "", email: "", company: "", goal: "" });
    const [sent, setSent] = useState(false);

    const submit = (e) => {
        e.preventDefault();
        const body = [
            `Name: ${form.name}`,
            `Email: ${form.email}`,
            `Company: ${form.company}`,
            `Goal: ${form.goal}`,
        ].join("\n");
        window.location.href = `${mailtoConsult}&body=${encodeURIComponent(body)}`;
        setSent(true);
    };

    return (
        <section id="contact" data-testid="contact-form-section" className="bg-lavender px-5 py-14 sm:py-20">
            <div
                ref={ref}
                className={`reveal mx-auto grid max-w-5xl gap-8 md:grid-cols-2 ${visible ? "is-visible" : ""}`}
            >
                <div>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--brand)] ring-1 ring-black/5">
                        {contactCopy.kicker}
                    </span>
                    <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight text-[#0a0a0b] sm:text-4xl">
                        {contactCopy.title}
                    </h2>
                    <p className="mt-3 max-w-md text-[15px] leading-relaxed text-[#5b5b66]">{contactCopy.subtitle}</p>

                    <div className="mt-7 space-y-3">
                        <a
                            data-testid="contact-email-link"
                            href={mailtoConsult}
                            className="flex items-center gap-3 rounded-2xl bg-white p-3.5 ring-1 ring-black/5 transition-colors hover:ring-[var(--brand)]/30"
                        >
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand-soft)] text-[var(--brand)]">
                                <Mail className="h-5 w-5" />
                            </span>
                            <span className="text-sm font-medium text-[#0a0a0b]">{brand.email}</span>
                        </a>
                        <a
                            href={`tel:${brand.phoneHref}`}
                            className="flex items-center gap-3 rounded-2xl bg-white p-3.5 ring-1 ring-black/5 transition-colors hover:ring-[var(--brand)]/30"
                        >
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand-soft)] text-[var(--brand)]">
                                <Phone className="h-5 w-5" />
                            </span>
                            <span className="text-sm font-medium text-[#0a0a0b]">{brand.phone}</span>
                        </a>
                        <div className="flex items-center gap-3 rounded-2xl bg-white p-3.5 ring-1 ring-black/5">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand-soft)] text-[var(--brand)]">
                                <MapPin className="h-5 w-5" />
                            </span>
                            <span className="text-sm font-medium text-[#0a0a0b]">{brand.address}</span>
                        </div>
                    </div>
                </div>

                {sent ? (
                    <div
                        data-testid="contact-form-success"
                        className="rounded-3xl bg-white p-6 text-center shadow-[0_20px_55px_rgba(15,23,42,0.10)] ring-1 ring-black/5 sm:p-7"
                    >
                        <Check className="mx-auto h-10 w-10 text-[var(--brand)]" />
                        <h3 className="mt-4 font-display text-2xl font-bold text-[#0a0a0b]">Your email app should open</h3>
                        <p className="mt-3 text-sm leading-relaxed text-[#5b5b66]">
                            If it doesn&apos;t, email {CONTACT.email} or call {CONTACT.phoneDisplay}.
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={submit}
                        className="rounded-3xl bg-white p-6 shadow-[0_20px_55px_rgba(15,23,42,0.10)] ring-1 ring-black/5 sm:p-7"
                    >
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-[#5b5b66]">
                                    Full name
                                </label>
                                <input
                                    id="contact-name"
                                    data-testid="contact-form-name-input"
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    placeholder="Jane Tremblay"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-[#5b5b66]">
                                    Company email
                                </label>
                                <input
                                    id="contact-email"
                                    data-testid="contact-form-email-input"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="jane@company.ca"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label htmlFor="contact-company" className="mb-1.5 block text-sm font-medium text-[#5b5b66]">
                                    Company & team size
                                </label>
                                <input
                                    id="contact-company"
                                    data-testid="contact-form-company-input"
                                    required
                                    value={form.company}
                                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                                    placeholder="Northline — 8-person sales team"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label htmlFor="contact-goal" className="mb-1.5 block text-sm font-medium text-[#5b5b66]">
                                    Primary growth goal
                                </label>
                                <select
                                    id="contact-goal"
                                    data-testid="contact-form-goal-select"
                                    required
                                    value={form.goal}
                                    onChange={(e) => setForm({ ...form, goal: e.target.value })}
                                    className={`${inputCls} appearance-none`}
                                >
                                    <option value="" disabled>
                                        Select a focus
                                    </option>
                                    {contactCopy.goals.map((g) => (
                                        <option key={g} value={g}>
                                            {g}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="contact-form-submit-button"
                            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3.5 text-base font-semibold text-white shadow-[0_12px_28px_rgba(43,80,255,0.38)] transition-transform hover:-translate-y-0.5 active:translate-y-0"
                        >
                            Book the diagnostic
                        </button>
                        <p className="mt-3 text-center text-xs text-[#8a8a95]">
                            No commitment. We reply from St. Catharines.
                        </p>
                    </form>
                )}
            </div>
        </section>
    );
}
